import { makeAutoObservable, runInAction } from 'mobx'
import { getUrlEndPath } from 'shared/lib'
import type { IDropdownItem } from 'shared/ui'
import { IntegrationKey, type Integration } from 'entities/Integrations'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { type ChatbotBooking, type IBookingType } from 'entities/Chatbot'
import { ManageIntegrationConnectionStore } from 'features/ManageIntegrationConnection'
import { MeetingsStore } from 'widgets/Meetings/store/meetingsStore'
import type { ICalendlyIntegrationMeetingCustom } from 'pages/chatbot/pages/agentBookAppointment/pages/connectCalendar/store/types'
import type { IEventLinkDropdownItem } from 'pages/chatbot/pages/agentBookAppointment/pages/connectCalendar/store/connectCalendar'
import { CalendlyFieldsStore } from 'pages/chatbot/store/calendlyFieldsStore'

export class BookingSectionStore {
  isCalendlyConnected = false
  isLoading = false
  private _integrationMap: Map<number | string, Integration> = new Map()
  private _manageIntegrationConnectionStore = new ManageIntegrationConnectionStore()
  private _meetingsStore: MeetingsStore
  private _calendlyFieldsStore: CalendlyFieldsStore

  constructor(readonly entities: IEntitiesStore, private _booking: ChatbotBooking) {
    this._meetingsStore = new MeetingsStore({ contact: null }, this.entities)
    this._calendlyFieldsStore = new CalendlyFieldsStore(this.entities)
    makeAutoObservable(this)
  }

  get calendlyFieldsStore() {
    return this._calendlyFieldsStore
  }

  get bookingEnabled() {
    return this._booking.bookingEnabled
  }

  setBookingEnabled = (value: boolean) => {
    this._booking.setEnabled(value)
  }

  get bookingType() {
    return this._booking.type
  }

  setBookingType = (type: IBookingType) => {
    this._booking.setType(type)
  }

  get smartBookingEnabled() {
    return this._booking.smartBookingEnabled
  }

  setSmartBookingEnabled = (value: boolean) => {
    this._booking.setSmartBookingEnabled(value)
  }

  get eventLink(): IEventLinkDropdownItem | null {
    const link = this._booking.link
    if (!link) return null
    return {
      id: link.id,
      label: link.name,
      labelRight: getUrlEndPath(link.value),
      labelRightVariant: 'body-md-regular',
      data: {
        id: link.id,
        name: link.name,
        scheduling_url: link.value,
      },
    }
  }

  setIsCalendlyConnected = (value: boolean) => {
    this.isCalendlyConnected = value
  }

  get isMeetingLoading() {
    return this._meetingsStore.loading
  }

  get search() {
    return this._meetingsStore.search
  }

  setSearch = (value: string) => {
    this._meetingsStore.setSearch(value)
  }

  get connectionList(): IDropdownItem[] {
    const list: IDropdownItem[] = []

    this.entities.integrationsStore
      .getIntegrations([IntegrationKey.calendly, IntegrationKey.hubspot])
      .forEach((item) => {
        this._integrationMap.set(item.id, item)

        if (item.key === IntegrationKey.calendly) {
          if (item.connected) {
            this.setIsCalendlyConnected(true)
            this._meetingsStore.onSelectedIntegration(item)
          }
          list.push({
            id: item.id,
            iconL: 'calendly',
            label: 'Calendly',
            labelRight: item.connected ? null : 'Connect',
            labelRightVariant: 'body-md-regular',
          })
        }

        if (item.key === IntegrationKey.hubspot) {
          list.push({
            id: item.id,
            iconL: 'hubspot',
            label: 'HubSpot meetings',
            disabled: true,
          })
        }
      })

    return list
  }

  get meetingsList(): IDropdownItem[] {
    return (
      this._meetingsStore.filteredMeetings?.map((meeting) => {
        const calendarEvent: ICalendlyIntegrationMeetingCustom = {
          name: meeting.name,
          scheduling_url: 'scheduling_url' in meeting ? meeting.scheduling_url : '',
          id: meeting.id,
        }
        return {
          id: meeting.id,
          label: calendarEvent.name,
          labelRight: getUrlEndPath(calendarEvent.scheduling_url),
          labelRightVariant: 'body-md-regular',
          data: calendarEvent,
        }
      }) ?? []
    )
  }

  setConnection = async (connection: IDropdownItem) => {
    if (this.isCalendlyConnected) return
    const integration = this._integrationMap.get(connection.id)
    if (!integration) return

    runInAction(() => {
      this.isLoading = true
    })
    try {
      await this._manageIntegrationConnectionStore.connect(integration)
    } finally {
      runInAction(() => {
        this.isLoading = false
      })
    }
  }

  setLink = (link: IDropdownItem) => {
    const item = link as IEventLinkDropdownItem

    this._booking.setLink({
      value: item.data.scheduling_url,
      id: String(item.data.id),
      name: item.data.name,
    })

    this._calendlyFieldsStore.fetchFields({
      params: String(item.id),
    })
  }
}
